import { t } from "i18next";

import {
  endOfDay,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths
} from "date-fns";

export interface DateRangeFilterValue {
  from?: string;
  to?: string;
  presetId?: string;
}

export interface DateRangePreset {
  id: string;
  label: string;
  getRange: (now: Date) => { from: Date; to: Date };
}

export type DateRangePresetSet = "past";

interface DateRangePresetDefinition {
  id: string;
  getRange: (now: Date) => { from: Date; to: Date };
}

const toIsoDay = (date: Date): string => format(date, "yyyy-MM-dd");

const getDatePresetLabel = (id: string): string =>
  t(`admin.dataTable.filters.datePresets.${id}`);

const DATE_RANGE_PRESET_DEFINITIONS: DateRangePresetDefinition[] = [
  {
    id: "today",
    getRange: (now) => ({ from: startOfDay(now), to: endOfDay(now) })
  },
  {
    id: "yesterday",
    getRange: (now) => {
      const yesterday = subDays(now, 1);
      return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
    }
  },
  {
    id: "last7",
    getRange: (now) => ({
      from: startOfDay(subDays(now, 6)),
      to: endOfDay(now)
    })
  },
  {
    id: "last30",
    getRange: (now) => ({
      from: startOfDay(subDays(now, 29)),
      to: endOfDay(now)
    })
  },
  {
    id: "thisMonth",
    getRange: (now) => ({ from: startOfMonth(now), to: endOfDay(now) })
  },
  {
    id: "lastMonth",
    getRange: (now) => {
      const prev = subMonths(now, 1);
      return { from: startOfMonth(prev), to: endOfMonth(prev) };
    }
  }
];

export const getPresetsForSet = (
  _set: DateRangePresetSet = "past"
): DateRangePreset[] =>
  DATE_RANGE_PRESET_DEFINITIONS.map(({ id, getRange }) => ({
    id,
    label: getDatePresetLabel(id),
    getRange
  }));

export const buildPresetValue = (
  preset: DateRangePreset,
  now: Date = new Date()
): DateRangeFilterValue => {
  const { from, to } = preset.getRange(now);
  return {
    from: toIsoDay(from),
    to: toIsoDay(to),
    presetId: preset.id
  };
};

export const isDateRangeFilterValue = (
  value: unknown
): value is DateRangeFilterValue => {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<DateRangeFilterValue>;
  const fromOk = v.from === undefined || typeof v.from === "string";
  const toOk = v.to === undefined || typeof v.to === "string";
  return fromOk && toOk;
};

export const isDateRangeEmpty = (value?: DateRangeFilterValue): boolean =>
  !value || (!value.from && !value.to);

const parseDayStart = (iso?: string): number | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  const ts = d.getTime();
  return Number.isFinite(ts) ? startOfDay(d).getTime() : null;
};

const parseDayEnd = (iso?: string): number | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  const ts = d.getTime();
  return Number.isFinite(ts) ? endOfDay(d).getTime() : null;
};

export const isTimestampInRange = (
  ts: number | null | undefined,
  value: DateRangeFilterValue | undefined
): boolean => {
  if (isDateRangeEmpty(value)) return true;
  if (ts == null || !Number.isFinite(ts)) return false;

  let fromMs = parseDayStart(value?.from);
  let toMs = parseDayEnd(value?.to);

  if (fromMs != null && toMs != null && fromMs > toMs) {
    const swap = fromMs;
    fromMs = parseDayStart(value?.to);
    toMs = parseDayEnd(value?.from);
    if (fromMs == null) fromMs = swap;
  }

  if (fromMs != null && ts < fromMs) return false;
  if (toMs != null && ts > toMs) return false;
  return true;
};

const formatIsoDayShort = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (!Number.isFinite(d.getTime())) return iso;
  return format(d, "dd.MM.yyyy");
};

export const formatRangeLabel = (
  value: DateRangeFilterValue | undefined,
  presets: DateRangePreset[]
): string => {
  if (isDateRangeEmpty(value)) return t("admin.dataTable.filters.allDates");
  if (value?.presetId) {
    const preset = presets.find((p) => p.id === value.presetId);
    if (preset) return preset.label;
  }
  const from = value?.from ? formatIsoDayShort(value.from) : "";
  const to = value?.to ? formatIsoDayShort(value.to) : "";
  if (from && to) return `${from} – ${to}`;
  if (from) return `${from} →`;
  if (to) return `← ${to}`;
  return t("admin.dataTable.filters.allDates");
};
